/**
 * App reset / "load the latest version" helper.
 *
 * The PWA service worker can keep serving a stale app shell after a deploy.
 * This utility removes everything the browser uses to serve a cached build —
 * the service worker registrations and the Cache Storage entries — then reloads
 * so the freshest `index.html` (and its new hashed bundles) is fetched from the
 * server. It intentionally does NOT clear IndexedDB, so offline data / queued
 * submissions survive a "reload latest version".
 *
 * Used by the ErrorBoundary recovery button and any explicit "reload" action.
 */
export async function forceReloadLatest(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
  } catch {
    /* best-effort */
  }
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch {
    /* best-effort */
  }
  // No SW controls the page now and the cache shell is gone, so a normal reload
  // fetches index.html fresh from the server (it is served no-cache).
  window.location.reload();
}
